import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do Colorado law firms get more cases from SEO?",
    a: "Colorado law firms win cases from SEO by ranking for high-intent local terms like 'personal injury lawyer Denver' or 'family law attorney Colorado Springs', backed by FAQ-rich practice-area pages, real attorney bios with credentials, and proper LegalService + Attorney schema. We build the on-page foundation, the Google Business Profile, and the review velocity that compounds into qualified consultations.",
  },
  {
    q: "How long does law firm SEO take to work in Colorado?",
    a: "Most Colorado law firms see meaningful Map Pack movement in 90–120 days and stronger organic consultation requests in 4–6 months. Legal is one of the more competitive verticals — consistent monthly content, links, and review acquisition are what separate winners from firms that quit too early.",
  },
  {
    q: "Do you do GEO AI for Colorado law firms?",
    a: "Yes — and it's becoming a competitive moat. We optimize firm content so AI engines like ChatGPT, Perplexity, and Google AI Overviews cite you when potential clients ask 'best personal injury lawyer in Denver' or 'top family law attorney in Colorado Springs.' Almost no Colorado law firms are doing this yet.",
  },
  {
    q: "What practice areas does Colorado Web Impressions work with?",
    a: "Personal injury, family law, criminal defense, estate planning, business law, and immigration are our most common Colorado law firm engagements. We build dedicated practice-area pages, schema, and review systems for each one — not a generic 'attorney' page.",
  },
  {
    q: "How much does law firm SEO cost in Colorado?",
    a: "Our Growth Marketing package is $1,650/month and covers SEO, Google Maps, GEO AI, and social — the full stack most Colorado firms need. Larger multi-location firms or highly competitive PI markets may need a custom plan, which we'll quote transparently up front.",
  },
];

export const Route = createFileRoute("/law-firm-seo-colorado")({
  head: () =>
    industryMarketingHead("Law Firm", "law firm", "law-firm-seo-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Law Firm"
      industryLower="law firm"
      slug="law-firm-seo-colorado"
      subheadline="We help Colorado law firms turn organic search and Google Maps into a steady pipeline of qualified consultations — across personal injury, family law, and criminal defense."
      painPoints={[
        "Spending heavily on Google Ads with no SEO foundation underneath",
        "Losing local searches to national lead-gen sites like Avvo and FindLaw",
        "Invisible in Google Maps despite years of strong client outcomes",
      ]}
      serviceDescriptions={{
        seo: "Rank for practice-area terms ('personal injury lawyer Denver', 'family law Colorado Springs') with proper LegalService + Attorney schema.",
        googleMaps: "Dominate the local Map Pack so consultations come to you instead of Avvo or FindLaw.",
        webDesign: "Authoritative, conversion-tested firm websites with attorney bios, credentials, and clear consultation paths.",
        ppc: "Profitable, accountable Google Ads tuned to your practice areas and case-value math.",
        geoAi: "Get cited when Coloradans ask ChatGPT or Gemini for the best lawyer in their city.",
        socialMedia: "Trust-building content that warms up future clients before they ever pick up the phone.",
      }}
      testimonial={{
        quote:
          "Our consultation requests doubled within six months. Chris is the most responsive marketer we've worked with — period.",
        role: "Managing Partner, Personal Injury Firm",
        city: "Denver",
      }}
      faqs={FAQS}
      industrySpokes={[
        {
          "to": "/law-firm-google-maps-colorado",
          "label": "Law firm Google Maps in Colorado",
          "description": "Map Pack ranking and GBP optimization for Colorado law firms."
        },
        {
          "to": "/law-firm-ppc-colorado",
          "label": "Law firm PPC in Colorado",
          "description": "Practice-area Google Ads and LSA for Colorado attorneys."
        }
      ]}
      cityHubLinks={[
        {
          "to": "/colorado-springs",
          "label": "Law firm marketing in Colorado Springs",
          "description": "Springs attorney SEO, Maps, and intake-focused web."
        },
        {
          "to": "/denver-digital-marketing",
          "label": "Law firm marketing in Denver",
          "description": "Compete in Denver's saturated legal market with practice-area SEO."
        },
        {
          "to": "/boulder-digital-marketing",
          "label": "Law firm marketing in Boulder",
          "description": "Boulder attorney SEO and lead generation."
        }
      ]}
    />
  ),
});
